import type {
  AppointmentSummary,
  ApiKeySummary,
  AvailablePhoneNumberSummary,
  AvailabilityRuleSummary,
  BusinessProfileSummary,
  CalendarSubscriptionSummary,
  CallEventSummary,
  ConversationDetailSummary,
  ConversationListSummary,
  KnowledgeItemSummary,
  NotificationSettingsSummary,
  PhoneNumberSummary,
  PlatformBootstrap,
  ReceptionistConfigSummary,
  ServiceSummary,
  StaffSummary,
  WebhookEndpointSummary,
  WorkspaceMemberSummary,
  WorkspaceSummary,
} from "./types.js";
import { configureAuthUrl, getAuthToken, getAuthUrl, neonAuthConfigured } from "./auth.js";
import { industryTemplates } from "../../platform/templates.js";

export class DashboardRequestError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "DashboardRequestError";
  }
}

function fallbackTemplateAccent(key: string): string {
  const accents: Record<string, string> = {
    mortgage: "#335f91",
    dentist: "#2d6b57",
    trades: "#6b4d2d",
    beauty: "#95613f",
    professional: "#4e5f7c",
    restaurant: "#8a3d32",
  };

  return accents[key] ?? "#2d6b57";
}

function fallbackFieldLabel(field: string): string {
  return field
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function fallbackIndustryTemplates(): PlatformBootstrap["templates"] {
  return industryTemplates.map((template) => ({
    id: `template-${template.key}`,
    key: template.key,
    name: template.name,
    description: template.description,
    accent: fallbackTemplateAccent(template.key),
    defaultServices: template.defaultServices,
    defaultFields: template.defaultFields.map(fallbackFieldLabel),
    flowGoals: template.flowGoals,
  }));
}

export const fallbackBootstrap: PlatformBootstrap = {
  project: {
    neonProjectId: "shy-credit-93020644",
    neonRegion: "aws-ap-southeast-2",
    neonAuthUrl: getAuthUrl(),
    twilioMode: "platform-account",
    emailNotificationsConfigured: false,
    calendarSubscriptionsConfigured: false,
  },
  metrics: {
    callsToday: 42,
    bookedToday: 16,
    activeWorkspaces: 6,
    failedDeliveries: 1,
  },
  templates: fallbackIndustryTemplates(),
  workspaces: [
    {
      id: "workspace-dental",
      name: "Harbour Dental Studio",
      industryKey: "dentist",
      country: "NZ",
      status: "active",
      ownerEmail: "ops@harbourdental.co.nz",
      phoneNumber: "+64 9 555 0188",
      setupProgress: 100,
    },
    {
      id: "workspace-beauty",
      name: "Sora Skin Clinic",
      industryKey: "beauty",
      country: "AU",
      status: "onboarding",
      ownerEmail: "hello@soraskin.com.au",
      phoneNumber: "+61 2 5550 0192",
      setupProgress: 68,
    },
    {
      id: "workspace-trades",
      name: "Auckland Heat & Plumbing",
      industryKey: "trades",
      country: "NZ",
      status: "onboarding",
      ownerEmail: "jobs@aucklandheat.co.nz",
      phoneNumber: "+64 9 555 0142",
      setupProgress: 58,
    },
    {
      id: "workspace-professional",
      name: "Civic Legal & Property",
      industryKey: "professional",
      country: "NZ",
      status: "draft",
      ownerEmail: "hello@civicproperty.co.nz",
      phoneNumber: "+64 9 555 0175",
      setupProgress: 44,
    },
    {
      id: "workspace-restaurant",
      name: "Kauri Room",
      industryKey: "restaurant",
      country: "NZ",
      status: "draft",
      ownerEmail: "bookings@kauriroom.co.nz",
      setupProgress: 38,
    },
    {
      id: "workspace-plaxo",
      name: "Plaxo Mortgage Appointments",
      industryKey: "mortgage",
      country: "NZ",
      status: "active",
      ownerEmail: "team@plaxo.co.nz",
      phoneNumber: "+64 9 555 0110",
      setupProgress: 100,
    },
  ],
  appointments: [
    {
      id: "appt-1",
      workspaceId: "workspace-dental",
      customerName: "Ava Thompson",
      serviceName: "Emergency tooth pain",
      startsAt: new Date(Date.now() + 1000 * 60 * 60 * 3).toISOString(),
      status: "confirmed",
      calendarSync: "google",
      source: "assistant",
      staffName: "Dr Amelia Hart",
      durationMinutes: 45,
      aiAction: "booked",
      confirmationStatus: "confirmed",
      callerSummary: "Ava reported tooth pain and accepted the earliest emergency slot.",
      transcriptExcerpt: "Caller asked for the earliest available emergency appointment and confirmed Tuesday morning works.",
    },
    {
      id: "appt-2",
      workspaceId: "workspace-beauty",
      customerName: "Mia Chen",
      serviceName: "Facial consult",
      startsAt: new Date(Date.now() + 1000 * 60 * 60 * 26).toISOString(),
      status: "pending",
      calendarSync: "ics",
      source: "assistant",
      staffName: "Maya Singh",
      durationMinutes: 30,
      aiAction: "suggested",
      confirmationStatus: "needs_confirmation",
      callerSummary: "Mia wants a facial consult and asked for staff confirmation before the deposit link.",
      transcriptExcerpt: "The assistant suggested tomorrow afternoon and marked the request for team confirmation.",
    },
    {
      id: "appt-3",
      workspaceId: "workspace-restaurant",
      customerName: "Noah Williams",
      serviceName: "Dinner table for 6",
      startsAt: new Date(Date.now() + 1000 * 60 * 60 * 52).toISOString(),
      status: "confirmed",
      calendarSync: "none",
      source: "manual",
      staffName: "Front desk",
      durationMinutes: 90,
      aiAction: "none",
      confirmationStatus: "confirmed",
      callerSummary: "Noah booked a dinner table for six with dietary notes.",
      transcriptExcerpt: "Team entered the booking after the caller asked about a larger table.",
    },
    {
      id: "appt-4",
      workspaceId: "workspace-dental",
      customerName: "Leo Martin",
      serviceName: "Hygiene clean",
      startsAt: new Date(Date.now() + 1000 * 60 * 60 * 30).toISOString(),
      status: "cancelled",
      calendarSync: "google",
      source: "assistant",
      staffName: "Sophie Allen",
      durationMinutes: 60,
      aiAction: "booked",
      confirmationStatus: "cancelled",
      callerSummary: "Leo cancelled and asked to be called next week.",
      transcriptExcerpt: "Caller could not attend the hygiene clean and asked for a new time next week.",
    },
  ],
  calls: [
    {
      id: "call-1",
      workspaceId: "workspace-dental",
      direction: "inbound",
      customerName: "Ava Thompson",
      phoneNumber: "+64 21 555 018",
      status: "completed",
      outcome: "Booked emergency slot",
      startedAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    },
    {
      id: "call-2",
      workspaceId: "workspace-beauty",
      direction: "outbound",
      customerName: "Mia Chen",
      phoneNumber: "+61 412 555 018",
      status: "completed",
      outcome: "Deposit link requested",
      startedAt: new Date(Date.now() - 1000 * 60 * 46).toISOString(),
    },
    {
      id: "call-3",
      workspaceId: "workspace-plaxo",
      direction: "inbound",
      customerName: "Grace Li",
      phoneNumber: "+64 22 555 303",
      status: "live",
      outcome: "Adviser callback booked",
      startedAt: new Date(Date.now() - 1000 * 60 * 4).toISOString(),
    },
  ],
  services: [
    {
      id: "service-dental-emergency",
      workspaceId: "workspace-dental",
      name: "Emergency tooth pain",
      description: "Same-day triage for urgent pain, swelling, or broken teeth.",
      durationMinutes: 45,
      bufferMinutes: 15,
      active: true,
      priceLabel: "From $180",
      bookingRequired: true,
      humanApprovalRequired: false,
    },
    {
      id: "service-dental-hygiene",
      workspaceId: "workspace-dental",
      name: "Hygiene clean",
      description: "Routine clean and check-in with hygienist.",
      durationMinutes: 60,
      bufferMinutes: 10,
      active: true,
      priceLabel: "$165",
      bookingRequired: true,
      humanApprovalRequired: false,
    },
    {
      id: "service-beauty-facial",
      workspaceId: "workspace-beauty",
      name: "Facial consult",
      description: "Skin goals, treatment suitability, and patch-test guidance.",
      durationMinutes: 30,
      bufferMinutes: 15,
      active: true,
      priceLabel: "Free consult",
      bookingRequired: true,
      humanApprovalRequired: true,
    },
    {
      id: "service-trades-urgent",
      workspaceId: "workspace-trades",
      name: "Urgent repair intake",
      description: "Capture the job type, address, access notes, and urgency for the on-call team.",
      durationMinutes: 45,
      bufferMinutes: 0,
      active: true,
      priceLabel: "Quote required",
      bookingRequired: true,
      humanApprovalRequired: true,
    },
    {
      id: "service-professional-callback",
      workspaceId: "workspace-professional",
      name: "Document checklist callback",
      description: "Collect matter type, deadline, and document context before a qualified person follows up.",
      durationMinutes: 30,
      bufferMinutes: 0,
      active: true,
      priceLabel: "Initial callback",
      bookingRequired: true,
      humanApprovalRequired: true,
    },
    {
      id: "service-plaxo-callback",
      workspaceId: "workspace-plaxo",
      name: "Mortgage adviser callback",
      description: "Book a licensed adviser call. Mortgage specifics are handled by the adviser, not the assistant.",
      durationMinutes: 30,
      bufferMinutes: 0,
      active: true,
      priceLabel: "No charge",
      bookingRequired: true,
      humanApprovalRequired: true,
    },
  ],
  staffMembers: [
    {
      id: "staff-dental-1",
      workspaceId: "workspace-dental",
      name: "Dr Amelia Hart",
      role: "Dentist",
      phoneNumber: "+64 21 555 018",
      calendarProvider: "google",
      active: true,
    },
    {
      id: "staff-dental-2",
      workspaceId: "workspace-dental",
      name: "Sophie Allen",
      role: "Hygienist",
      phoneNumber: "+64 21 555 019",
      calendarProvider: "google",
      active: true,
    },
    {
      id: "staff-beauty-1",
      workspaceId: "workspace-beauty",
      name: "Maya Singh",
      role: "Senior therapist",
      phoneNumber: "+64 21 555 020",
      calendarProvider: "apple",
      active: true,
    },
  ],
  workspaceMembers: [
    {
      id: "member-dental-owner",
      workspaceId: "workspace-dental",
      email: "ops@harbourdental.co.nz",
      role: "owner",
      status: "active",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 21).toISOString(),
    },
    {
      id: "member-dental-manager",
      workspaceId: "workspace-dental",
      email: "frontdesk@harbourdental.co.nz",
      role: "manager",
      status: "active",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    },
    {
      id: "member-beauty-owner",
      workspaceId: "workspace-beauty",
      email: "hello@soraskin.com.au",
      role: "owner",
      status: "active",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
    },
    {
      id: "member-trades-owner",
      workspaceId: "workspace-trades",
      email: "jobs@aucklandheat.co.nz",
      role: "owner",
      status: "active",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
    },
    {
      id: "member-professional-owner",
      workspaceId: "workspace-professional",
      email: "hello@civicproperty.co.nz",
      role: "owner",
      status: "active",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
    },
  ],
  availabilityRules: [
    {
      id: "availability-dental-mon",
      workspaceId: "workspace-dental",
      staffMemberId: "staff-dental-1",
      weekday: 1,
      startsAt: "08:30",
      endsAt: "17:00",
      active: true,
    },
    {
      id: "availability-dental-tue",
      workspaceId: "workspace-dental",
      staffMemberId: "staff-dental-2",
      weekday: 2,
      startsAt: "09:00",
      endsAt: "16:30",
      active: true,
    },
    {
      id: "availability-beauty-wed",
      workspaceId: "workspace-beauty",
      staffMemberId: "staff-beauty-1",
      weekday: 3,
      startsAt: "10:00",
      endsAt: "19:00",
      active: true,
    },
  ],
  businessProfiles: [
    {
      workspaceId: "workspace-dental",
      legalName: "Harbour Dental Limited",
      tradingName: "Harbour Dental Studio",
      timezone: "Pacific/Auckland",
      websiteUrl: "https://harbourdental.co.nz",
      publicPhone: "+64 9 555 0188",
      defaultLanguage: "en",
      summary: "Auckland dental clinic focused on urgent care, hygiene, and family appointments.",
      brandTone: "Calm, practical, and reassuring",
    },
    {
      workspaceId: "workspace-beauty",
      tradingName: "Sora Skin Clinic",
      timezone: "Australia/Sydney",
      websiteUrl: "https://soraskin.com.au",
      publicPhone: "+61 2 5550 0192",
      defaultLanguage: "en",
      summary: "Skin clinic handling consults, patch tests, and deposit-sensitive bookings.",
      brandTone: "Warm and concise",
    },
    {
      workspaceId: "workspace-trades",
      tradingName: "Auckland Heat & Plumbing",
      timezone: "Pacific/Auckland",
      websiteUrl: "https://aucklandheat.co.nz",
      publicPhone: "+64 9 555 0142",
      defaultLanguage: "en",
      summary: "Home services team that needs urgent repair appointments, site visit booking, job notes, and after-hours messages.",
      brandTone: "Direct, practical, and local",
    },
    {
      workspaceId: "workspace-professional",
      tradingName: "Civic Legal & Property",
      timezone: "Pacific/Auckland",
      websiteUrl: "https://civicproperty.co.nz",
      publicPhone: "+64 9 555 0175",
      defaultLanguage: "en",
      summary: "Professional services office handling new enquiry triage, callback booking, and document checklist collection.",
      brandTone: "Structured, careful, and plain-spoken",
    },
    {
      workspaceId: "workspace-plaxo",
      tradingName: "Plaxo Mortgage Appointments",
      timezone: "Pacific/Auckland",
      websiteUrl: "https://plaxo.co.nz",
      publicPhone: "+64 9 555 0110",
      defaultLanguage: "en",
      summary: "Appointment-only mortgage adviser desk. The assistant books calls and leaves advice, calculations, rates, approvals, and product recommendations to advisers.",
      brandTone: "Calm, appointment-focused, and compliant",
    },
  ],
  knowledgeItems: [
    {
      id: "knowledge-dental-hours",
      workspaceId: "workspace-dental",
      title: "Opening hours",
      category: "profile",
      body: "Open Monday to Friday, 8:30am to 5:00pm. Urgent pain calls should be offered the earliest emergency slot.",
      active: true,
      status: "published",
      tags: ["hours", "urgent"],
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 18).toISOString(),
    },
    {
      id: "knowledge-dental-price",
      workspaceId: "workspace-dental",
      title: "Emergency appointment price",
      category: "service",
      body: "Emergency appointments start from $180. The assistant can share the starting price but must not promise a final treatment cost.",
      active: true,
      status: "published",
      tags: ["pricing", "service"],
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
    },
    {
      id: "knowledge-dental-policy",
      workspaceId: "workspace-dental",
      title: "Cancellation policy",
      category: "policy",
      body: "Ask callers to give at least 24 hours notice when cancelling. Same-day urgent changes should be escalated to the front desk.",
      active: true,
      status: "published",
      tags: ["policy"],
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 34).toISOString(),
    },
    {
      id: "knowledge-dental-forbidden",
      workspaceId: "workspace-dental",
      title: "Do not diagnose",
      category: "forbidden",
      body: "The assistant must not diagnose dental conditions or promise treatment outcomes. It can book care and explain what the clinic will check.",
      active: true,
      status: "published",
      tags: ["safety"],
      updatedAt: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
    },
  ],
  phoneNumbers: [
    {
      id: "phone-dental",
      workspaceId: "workspace-dental",
      phoneNumber: "+64 9 555 0188",
      country: "NZ",
      status: "active",
      inboundFlowId: "flow-dental-inbound",
      outboundFlowId: "flow-dental-outbound",
    },
    {
      id: "phone-plaxo",
      workspaceId: "workspace-plaxo",
      phoneNumber: "+64 9 555 0110",
      country: "NZ",
      status: "active",
      inboundFlowId: "flow-plaxo-inbound",
      outboundFlowId: "flow-plaxo-outbound",
    },
  ],
  receptionistConfigs: [
    {
      flowId: "flow-dental-inbound",
      workspaceId: "workspace-dental",
      industryKey: "dentist",
      version: 1,
      name: "Inbound booking flow",
      status: "active",
      greeting: "Hi, you've reached Harbour Dental Studio. How can I help today?",
      bookingMode: "book-needs-confirmation",
      requiredQuestions: ["What do you need help with?", "Which day works best?", "What name and phone number should we use?"],
      handoffRules: ["Severe pain or swelling", "Pricing exceptions", "Complaints"],
      systemPrompt: "Book the right dental appointment, avoid diagnosis, and escalate urgent symptoms to the team.",
      publishedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    },
    {
      flowId: "flow-plaxo-inbound",
      workspaceId: "workspace-plaxo",
      industryKey: "mortgage",
      version: 1,
      name: "Mortgage adviser appointment flow",
      status: "active",
      greeting: "Hi, you've reached Plaxo. I can help book a mortgage adviser call.",
      bookingMode: "book-needs-confirmation",
      requiredQuestions: ["What should the adviser call be about?", "What callback window works best?", "What name and contact details should the adviser use?"],
      handoffRules: ["Advice, calculation, rate, approval, or product recommendation requests", "Complaints or urgent settlement issues"],
      systemPrompt: "Stay appointment-only. Book adviser calls and do not provide mortgage advice, rate comparisons, borrowing calculations, approvals, or product recommendations.",
      publishedAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    },
  ],
  webhooks: [
    {
      id: "webhook-1",
      workspaceId: "workspace-dental",
      url: "https://cms.harbourdental.co.nz/api/callcenter",
      events: ["appointment.created", "call.completed"],
      status: "active",
      lastDeliveryStatus: "200 OK",
    },
    {
      id: "webhook-2",
      workspaceId: "workspace-beauty",
      url: "https://soraskin.com.au/hooks/booking",
      events: ["lead.captured", "appointment.created"],
      status: "active",
      lastDeliveryStatus: "retrying",
    },
  ],
  apiKeys: [
    {
      id: "api-key-1",
      workspaceId: "workspace-dental",
      name: "Dental CMS sync",
      keyPrefix: "ccp_live_5f2a91...",
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
      lastUsedAt: new Date(Date.now() - 1000 * 60 * 7).toISOString(),
    },
  ],
  notificationSettings: [],
  notificationDeliveries: [],
  notificationReads: [],
  usageSummaries: [
    {
      id: "workspace-dental:ops@harbourdental.co.nz",
      workspaceId: "workspace-dental",
      workspaceName: "Harbour Dental Studio",
      userEmail: "ops@harbourdental.co.nz",
      role: "owner",
      totalTokens: 184200,
      promptTokens: 96300,
      responseTokens: 72100,
      toolUsePromptTokens: 8900,
      thoughtsTokens: 0,
      cachedTokens: 6900,
      currentMonthTokens: 184200,
      lastSevenDaysTokens: 48200,
      sessions: 38,
      lastUsedAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    },
    {
      id: "workspace-plaxo:team@plaxo.co.nz",
      workspaceId: "workspace-plaxo",
      workspaceName: "Plaxo Mortgage Appointments",
      userEmail: "team@plaxo.co.nz",
      role: "owner",
      totalTokens: 126400,
      promptTokens: 70200,
      responseTokens: 45100,
      toolUsePromptTokens: 6200,
      thoughtsTokens: 0,
      cachedTokens: 4900,
      currentMonthTokens: 126400,
      lastSevenDaysTokens: 36900,
      sessions: 24,
      lastUsedAt: new Date(Date.now() - 1000 * 60 * 78).toISOString(),
    },
  ],
};

async function dashboardHeaders(): Promise<HeadersInit> {
  let token: string | null = null;
  try {
    token = await getAuthToken();
  } catch {
    throw new DashboardRequestError("Sign-in service is unavailable. Try again in a moment.", 503);
  }

  if (!token) {
    if (neonAuthConfigured) {
      throw new DashboardRequestError("Sign-in is required.", 401);
    }

    return {};
  }

  return {
    authorization: `Bearer ${token}`,
  };
}

const dashboardRequestTimeoutMs = 12000;

async function dashboardFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  { timeoutMs = dashboardRequestTimeoutMs }: { timeoutMs?: number } = {},
): Promise<Response> {
  const authHeaders = await dashboardHeaders();
  const requestInit: RequestInit = {
    ...init,
    headers: {
      ...authHeaders,
      ...(init.headers ?? {}),
    },
  };

  if (init.signal) {
    try {
      return await fetch(input, requestInit);
    } catch (error) {
      if (error instanceof TypeError) {
        throw new DashboardRequestError("Network request failed. Check your connection and retry.", 503);
      }
      throw error;
    }
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, {
      ...requestInit,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new DashboardRequestError("Request timed out. Retry when the connection is stable.", 408);
    }
    if (error instanceof TypeError) {
      throw new DashboardRequestError("Network request failed. Check your connection and retry.", 503);
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function responseErrorMessage(response: Response, fallback: string): Promise<string> {
  const payload = await response.json().catch(() => null) as {
    issues?: Array<{ message?: string; path?: string }>;
    message?: string;
  } | null;
  const issueMessages = payload?.issues
    ?.map((issue) => {
      const message = issue.message?.trim();
      if (!message) {
        return "";
      }

      return issue.path ? `${issue.path}: ${message}` : message;
    })
    .filter(Boolean);

  if (issueMessages?.length) {
    return issueMessages.join(" ");
  }

  return payload?.message ?? fallback;
}

export async function fetchBootstrap({ timeoutMs = 4500, requireAuth = false }: { timeoutMs?: number; requireAuth?: boolean } = {}): Promise<PlatformBootstrap> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await (requireAuth || neonAuthConfigured ? dashboardFetch : fetch)("/api/platform/bootstrap", {
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new DashboardRequestError(await responseErrorMessage(response, `Workspace request failed with ${response.status}.`), response.status);
    }

    const bootstrap = (await response.json()) as PlatformBootstrap;
    configureAuthUrl(bootstrap.project.neonAuthUrl);
    return bootstrap;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new DashboardRequestError("Workspace request timed out. Retry when the connection is stable.", 408);
    }

    if (requireAuth || neonAuthConfigured || !import.meta.env.DEV) {
      throw error;
    }

    return fallbackBootstrap;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function fetchAvailablePhoneNumbers(params: {
  country: "NZ" | "AU" | "US";
  contains?: string;
  areaCode?: string;
}): Promise<AvailablePhoneNumberSummary[]> {
  const query = new URLSearchParams();
  query.set("country", params.country);
  if (params.contains) query.set("contains", params.contains);
  if (params.areaCode) query.set("areaCode", params.areaCode);
  query.set("limit", "12");

  const response = await dashboardFetch(`/api/platform/phone-numbers/available?${query.toString()}`);
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Number search failed with ${response.status}.`), response.status);
  }
  const payload = (await response.json()) as { numbers?: AvailablePhoneNumberSummary[] };
  return payload.numbers ?? [];
}

export async function createAppointment(input: {
  workspaceId: string;
  customerName: string;
  phone?: string;
  email?: string;
  serviceName: string;
  staffMemberId?: string;
  startsAt: string;
  durationMinutes?: number;
  notes?: string;
  source?: "assistant" | "staff" | "website" | "manual";
}): Promise<AppointmentSummary> {
  const response = await dashboardFetch("/api/platform/appointments", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Appointment creation failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as AppointmentSummary;
}

export async function updateAppointment(input: {
  id: string;
  workspaceId: string;
  startsAt?: string;
  durationMinutes?: number;
  status?: "pending" | "confirmed" | "cancelled" | "completed" | "no_show";
}): Promise<AppointmentSummary> {
  const response = await dashboardFetch(`/api/platform/appointments/${encodeURIComponent(input.id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      durationMinutes: input.durationMinutes,
      startsAt: input.startsAt,
      status: input.status,
      workspaceId: input.workspaceId,
    }),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Appointment update failed with ${response.status}`), response.status);
  }
  return (await response.json()) as AppointmentSummary;
}

export async function appendCallEvent(input: {
  callId: string;
  workspaceId: string;
  eventType: "staff.note" | "staff.action" | "lead.action";
  text: string;
  payload?: Record<string, unknown>;
}): Promise<CallEventSummary> {
  const response = await dashboardFetch("/api/platform/call-events", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Call event save failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as CallEventSummary;
}

export interface OutboundCallResult {
  ok?: true;
  sessionId: string;
  callSid?: string;
  status?: string;
}

export async function createOutboundCall(input: {
  to: string;
  from?: string;
  customerName?: string;
  workspaceId: string;
  flowId?: string;
}): Promise<OutboundCallResult> {
  const response = await dashboardFetch("/api/calls/outbound", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Outbound call failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as OutboundCallResult;
}

export async function fetchConversation(id: string): Promise<ConversationDetailSummary> {
  const response = await dashboardFetch(`/api/conversations/${encodeURIComponent(id)}`);
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Conversation request failed with ${response.status}`), response.status);
  }
  return (await response.json()) as ConversationDetailSummary;
}

export async function fetchConversations(limit = 200): Promise<ConversationListSummary[]> {
  const query = new URLSearchParams({ limit: String(limit) });
  const response = await dashboardFetch(`/api/conversations?${query.toString()}`);
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Conversation list request failed with ${response.status}`), response.status);
  }
  return (await response.json()) as ConversationListSummary[];
}

export async function purchasePhoneNumber(input: {
  workspaceId: string;
  phoneNumber: string;
  country: "NZ" | "AU" | "US";
}): Promise<PhoneNumberSummary> {
  const response = await dashboardFetch("/api/platform/phone-numbers/purchase", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Number purchase failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as PhoneNumberSummary;
}

export async function connectExistingPhoneNumber(input: {
  workspaceId: string;
  phoneNumber: string;
  country: "NZ" | "AU" | "US";
}): Promise<PhoneNumberSummary> {
  const response = await dashboardFetch("/api/platform/phone-numbers/connect", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Number connect failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as PhoneNumberSummary;
}

export async function createWorkspace(input: {
  name: string;
  industryKey: WorkspaceSummary["industryKey"];
  country: WorkspaceSummary["country"];
  ownerEmail: string;
}): Promise<WorkspaceSummary> {
  const response = await dashboardFetch("/api/platform/workspaces", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Business setup failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as WorkspaceSummary;
}

export async function completeWorkspaceOnboarding(input: {
  workspaceId: string;
  industryKey?: WorkspaceSummary["industryKey"];
  name?: string;
}): Promise<WorkspaceSummary> {
  const response = await dashboardFetch("/api/platform/workspaces/complete-onboarding", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Onboarding completion failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as WorkspaceSummary;
}

export type OnboardingDraftSuggestion = {
  brandTone: string;
  greeting: string;
  handoffRules: string[];
  industryKey?: WorkspaceSummary["industryKey"];
  knowledgeBody: string;
  questions: string[];
  services: string[];
  summary: string;
};

export async function createOnboardingDraft(input: {
  workspaceId: string;
  businessName?: string;
  country?: WorkspaceSummary["country"];
  industryKey?: WorkspaceSummary["industryKey"];
  language: "en" | "zh";
  roughNotes?: string;
}): Promise<OnboardingDraftSuggestion> {
  const response = await dashboardFetch("/api/platform/onboarding/draft", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  }, { timeoutMs: 20000 });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Onboarding draft failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as OnboardingDraftSuggestion;
}

export async function saveReceptionistConfig(input: {
  workspaceId: string;
  greeting: string;
  bookingMode: ReceptionistConfigSummary["bookingMode"];
  requiredQuestions: string[];
  handoffRules: string[];
  systemPrompt: string;
}): Promise<ReceptionistConfigSummary> {
  const response = await dashboardFetch("/api/platform/receptionist-config", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Receptionist config save failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as ReceptionistConfigSummary;
}

export async function saveBusinessProfile(input: {
  workspaceId: string;
  legalName?: string;
  tradingName?: string;
  timezone: string;
  websiteUrl?: string;
  publicPhone?: string;
  defaultLanguage: BusinessProfileSummary["defaultLanguage"];
  summary?: string;
  brandTone?: string;
}): Promise<BusinessProfileSummary> {
  const response = await dashboardFetch("/api/platform/business-profile", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Business profile save failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as BusinessProfileSummary;
}

export async function saveKnowledgeItem(input: {
  id?: string;
  workspaceId: string;
  title: string;
  category: KnowledgeItemSummary["category"];
  body: string;
  sourceUrl?: string;
  status: "draft" | "published";
}): Promise<KnowledgeItemSummary> {
  const response = await dashboardFetch("/api/platform/knowledge-items", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Knowledge save failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as KnowledgeItemSummary;
}

export async function importWebsiteKnowledge(input: {
  workspaceId: string;
  url: string;
}): Promise<KnowledgeItemSummary> {
  const response = await dashboardFetch("/api/platform/knowledge-items/import-website", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Website import failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as KnowledgeItemSummary;
}

export async function saveNotificationSettings(input: {
  workspaceId: string;
  slackWebhookUrl?: string;
  telegramBotToken?: string;
  telegramChatId?: string;
  emailRecipients: string[];
  events: string[];
}): Promise<NotificationSettingsSummary> {
  const response = await dashboardFetch("/api/platform/notification-settings", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Notification settings save failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as NotificationSettingsSummary;
}

export async function markNotificationsRead(input: {
  workspaceId: string;
  notificationKeys: string[];
}): Promise<PlatformBootstrap["notificationReads"]> {
  const response = await dashboardFetch("/api/platform/notifications/read", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Notification read update failed with ${response.status}.`), response.status);
  }
  return (await response.json()) as PlatformBootstrap["notificationReads"];
}

export async function fetchCalendarSubscription(workspaceId: string): Promise<CalendarSubscriptionSummary> {
  const query = new URLSearchParams({ workspaceId });
  const response = await dashboardFetch(`/api/platform/calendar-subscription?${query.toString()}`);
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Calendar subscription request failed with ${response.status}`), response.status);
  }
  return (await response.json()) as CalendarSubscriptionSummary;
}

export async function createApiKey(input: {
  workspaceId: string;
  name: string;
}): Promise<ApiKeySummary & { apiKey: string }> {
  const response = await dashboardFetch("/api/platform/api-keys", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `API key creation failed with ${response.status}`), response.status);
  }
  return (await response.json()) as ApiKeySummary & { apiKey: string };
}

export async function createWebhookEndpoint(input: {
  workspaceId: string;
  url: string;
  events: string[];
}): Promise<WebhookEndpointSummary & { signingSecret: string }> {
  const response = await dashboardFetch("/api/platform/webhooks", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Webhook creation failed with ${response.status}`), response.status);
  }
  return (await response.json()) as WebhookEndpointSummary & { signingSecret: string };
}

export async function createAvailabilityRule(input: {
  workspaceId: string;
  staffMemberId?: string;
  weekday: number;
  startsAt: string;
  endsAt: string;
  active?: boolean;
}): Promise<AvailabilityRuleSummary> {
  const response = await dashboardFetch("/api/platform/availability-rules", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Availability rule creation failed with ${response.status}`), response.status);
  }
  return (await response.json()) as AvailabilityRuleSummary;
}

export async function updateAvailabilityRule(input: {
  id: string;
  workspaceId: string;
  staffMemberId?: string;
  weekday?: number;
  startsAt?: string;
  endsAt?: string;
  active?: boolean;
}): Promise<AvailabilityRuleSummary> {
  const response = await dashboardFetch(`/api/platform/availability-rules/${encodeURIComponent(input.id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      active: input.active,
      endsAt: input.endsAt,
      staffMemberId: input.staffMemberId,
      startsAt: input.startsAt,
      weekday: input.weekday,
      workspaceId: input.workspaceId,
    }),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Availability rule update failed with ${response.status}`), response.status);
  }
  return (await response.json()) as AvailabilityRuleSummary;
}

export async function createService(input: {
  workspaceId: string;
  name: string;
  description?: string;
  durationMinutes: number;
  bufferMinutes: number;
  active?: boolean;
}): Promise<ServiceSummary> {
  const response = await dashboardFetch("/api/platform/services", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Service creation failed with ${response.status}`), response.status);
  }
  return (await response.json()) as ServiceSummary;
}

export async function createStaffMember(input: {
  workspaceId: string;
  name: string;
  role?: string;
  phoneNumber?: string;
  calendarProvider: "google" | "outlook" | "apple" | "none";
  active?: boolean;
}): Promise<StaffSummary> {
  const response = await dashboardFetch("/api/platform/staff-members", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Staff member creation failed with ${response.status}`), response.status);
  }
  return (await response.json()) as StaffSummary;
}

export async function updateStaffMember(input: {
  id: string;
  workspaceId: string;
  name?: string;
  role?: string;
  phoneNumber?: string;
  calendarProvider?: "google" | "outlook" | "apple" | "none";
  active?: boolean;
}): Promise<StaffSummary> {
  const response = await dashboardFetch(`/api/platform/staff-members/${encodeURIComponent(input.id)}`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      active: input.active,
      calendarProvider: input.calendarProvider,
      name: input.name,
      phoneNumber: input.phoneNumber,
      role: input.role,
      workspaceId: input.workspaceId,
    }),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Staff member update failed with ${response.status}`), response.status);
  }
  return (await response.json()) as StaffSummary;
}

export async function createWorkspaceMember(input: {
  workspaceId: string;
  email: string;
  role: "manager" | "agent" | "viewer";
}): Promise<WorkspaceMemberSummary> {
  const response = await dashboardFetch("/api/platform/workspace-members", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new DashboardRequestError(await responseErrorMessage(response, `Workspace access save failed with ${response.status}`), response.status);
  }
  return (await response.json()) as WorkspaceMemberSummary;
}
